import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsInfoCircle } from 'react-icons/bs';

import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import { useOrgMembersBasic } from '#orgFeature/models/org/queries';

import { useLeadData } from '../../../../../contexts/LeadDataContext';
import { useLeadJobs } from '../../../../../contexts/LeadJobContext';
import MergeQueryModal from '../MergeQueryModal/MergeQueryModal';
import ProspectQuerySelector from '../ProspectQuerySelector/ProspectQuerySelector';

/**
 * Header section of the lead table
 * Contains search query selector and action buttons
 */
export const LeadTableHeader = ({ onExtend, actions, onResumeAssistant, onShowProtocol }) => {
	// Get data directly from contexts
	const {
		searchQueries,
		selectedSearchQuery,
		handleSearchQuerySelect,
		handleDeleteProspectQuery,
		handleRenameProspectQuery,
		totalCount,
	} = useLeadData();

	const { runningJobs, hasCancellingCompanyScraperJob, handleAbortSearchJob } = useLeadJobs();

	const { data: membersData } = useOrgMembersBasic();
	const orgMembers = membersData?.members ?? [];
	const orgMemberCount = orgMembers.length || 1;

	const [mergeSource, setMergeSource] = useState(null);
	const [queryToDelete, setQueryToDelete] = useState(null);

	const contactCount = totalCount;

	const handleMergeClick = (query) => {
		setMergeSource(query);
	};

	const handleMergeClose = () => {
		setMergeSource(null);
	};

	const handleMergeComplete = () => {
		setMergeSource(null);
	};

	const isSharedQuery = Boolean(selectedSearchQuery?.isShared);

	return (
		<div className="mb-3">
			<div className="flex items-center justify-between gap-2">
				<ProspectQuerySelector
					queries={searchQueries}
					selected={selectedSearchQuery}
					onSelect={handleSearchQuerySelect}
					onDelete={(queryId) =>
						setQueryToDelete(searchQueries.find((q) => q.id === queryId) ?? { id: queryId })
					}
					onRename={handleRenameProspectQuery}
					onMerge={handleMergeClick}
					contactCount={contactCount}
					onExtend={onExtend}
					onAbort={handleAbortSearchJob}
					runningJobs={runningJobs}
					isCancellingSearch={hasCancellingCompanyScraperJob}
					orgMemberCount={orgMemberCount}
					orgMembers={orgMembers}
					onResumeAssistant={onResumeAssistant}
					onShowProtocol={onShowProtocol}
				/>

				{actions}
			</div>

			{/* Shared query info banner */}
			{isSharedQuery && (
				<div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-success-700 text-xs">
					<BsInfoCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-success-700" />
					<span>
						Diese Liste wurde mit Ihnen geteilt.{' '}
						<span className="font-medium">Erweitern, Datenanreicherung, KI-Analyse und Zusammenführen</span>{' '}
						sind nur für eigene Listen verfügbar.
					</span>
				</div>
			)}

			<ConfirmModal
				isOpen={Boolean(queryToDelete)}
				onClose={() => setQueryToDelete(null)}
				onConfirm={async () => {
					await handleDeleteProspectQuery(queryToDelete.id);
					setQueryToDelete(null);
				}}
				title="Suchanfrage löschen"
				message={
					<>
						Möchten Sie die Suchanfrage „<strong>{queryToDelete?.queryName ?? ''}</strong>“ wirklich
						löschen? Alle Firmen und Kontakte dieser Suche werden aus der Ansicht entfernt.
					</>
				}
				confirmText="Suchanfrage löschen"
			/>

			{/* Merge Query Modal */}
			<MergeQueryModal
				isOpen={Boolean(mergeSource)}
				onClose={handleMergeClose}
				sourceQuery={mergeSource}
				queries={searchQueries}
				onMergeComplete={handleMergeComplete}
			/>
		</div>
	);
};

LeadTableHeader.propTypes = {
	onExtend: PropTypes.func.isRequired,
	actions: PropTypes.node,
	onResumeAssistant: PropTypes.func,
	onShowProtocol: PropTypes.func,
};
